import React, { useEffect, useRef, useState } from 'react';
import {
  makeStyles,
  shorthands,
  tokens,
  Spinner,
} from '@fluentui/react-components';
import Header from './Header';
import BulkActionBar, { BulkAction } from './BulkActionBar';
import { useSelection } from '../contexts/SelectionContext';
import { TableToolbarSlotProvider } from './tableToolbarSlot';

interface TablePageProps {
  /** Page heading. Rendered in the inline Header's title slot. Pass a string
   *  for the default styling, or a node for a custom title. */
  title: React.ReactNode;
  /** Header left-actions slot (e.g. the filter-panel toggle). Kept as a slot
   *  rather than a built-in so pages retain their own data-tour hooks and
   *  controlled tooltips. */
  headerLeftActions?: React.ReactNode;
  /** Header right-actions slot (e.g. Add button, Sync). */
  headerActions?: React.ReactNode;
  /** Suppress the Header's built-in "N selected" indicator. Pass-through —
   *  not auto-forced — so each page keeps its existing behavior. */
  hideSelectionInfo?: boolean;

  /** Filters sidebar rendered to the left of the content column. The sidebar
   *  owns its own collapsed state. Omit for pages without filters. */
  sidebar?: React.ReactNode;

  /** Loading state — replaces the content column body with a spinner. */
  loading?: boolean;
  loadingLabel?: string;
  /** Error message — replaces the content column body with an error banner. */
  error?: string | null;

  /** Search/filter bar contents (e.g. the live-search Input + result count).
   *  Rendered in a standard row above the table; the bulk-action cluster is
   *  appended to the trailing edge of this same row. */
  searchRow?: React.ReactNode;

  /** Current selection size. When provided, TablePage wires the Selection
   *  context (count + clear + unmount cleanup) so pages don't repeat the
   *  boilerplate, and drives the bulk-action bar. */
  selectedCount?: number;
  /** Clear the current selection. */
  onClearSelection?: () => void;
  /** Bulk actions offered when rows are selected. Rendered via BulkActionBar
   *  at the trailing edge of the search row. */
  bulkActions?: BulkAction[];
  /** Noun for the bulk-action count label, e.g. {singular:'order', plural:'orders'}. */
  bulkNoun?: { singular: string; plural: string };

  /** Main content — typically a <DataTable>, or a page-specific empty state. */
  children: React.ReactNode;
}

const useStyles = makeStyles({
  // Single elevated workspace card containing the inline Header + body.
  // Full-bleed (no gutter / square corners) on mobile.
  workspaceCard: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('8px'),
    boxShadow: tokens.shadow4,
    flexGrow: 1,
    // Floor to the viewport so flexGrow can't collapse the card to content
    // height when the nested flex height chain fails to resolve (gray gap
    // below the work surface on scroll). flexShrink:0 prevents re-squeeze.
    minHeight: 'calc(100vh - 64px)',
    flexShrink: 0,
    ...shorthands.margin('8px'),
    ...shorthands.overflow('hidden'),
    '@media (max-width: 768px)': {
      ...shorthands.margin('0'),
      ...shorthands.borderRadius('0'),
    },
  },
  // Hosts the filter sidebar + content column side-by-side. The borderTop is
  // the divider between the inline Header (chrome) and this row.
  body: {
    display: 'flex',
    alignItems: 'stretch',
    flexGrow: 1,
    minHeight: 0,
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
  },
  content: {
    flexGrow: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  searchRow: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('12px'),
    ...shorthands.padding('8px', '12px'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    flexShrink: 0,
    '@media (max-width: 768px)': {
      flexWrap: 'wrap',
    },
  },
  // Trailing cluster: the Columns toggle (portaled in by DataTable) + the
  // bulk-action bar, right-aligned as one group via marginLeft:auto.
  searchRowTrailing: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
    marginLeft: 'auto',
  },
  columnsSlot: {
    display: 'flex',
    alignItems: 'center',
  },
  loading: {
    ...shorthands.padding('40px'),
    textAlign: 'center',
  },
  error: {
    ...shorthands.padding('20px'),
    ...shorthands.margin('16px'),
    backgroundColor: '#fef0f0',
    ...shorthands.border('1px', 'solid', '#fcd4d4'),
    ...shorthands.borderRadius('4px'),
    color: '#c42b1c',
  },
});

/**
 * Composition shell for full-page table/list views (Orders, Customers, …).
 * Owns the shared skeleton — workspace card, inline Header, sidebar + content
 * layout, loading/error states, the search row, the bulk-action bar, and the
 * SelectionContext wiring — so each list page collapses to its columns,
 * filters, actions, and data. NOT for embedded tables (panels, detail-page
 * sections) — those use <DataTable> directly.
 */
const TablePage: React.FC<TablePageProps> = ({
  title,
  headerLeftActions,
  headerActions,
  hideSelectionInfo,
  sidebar,
  loading = false,
  loadingLabel = 'Loading…',
  error,
  searchRow,
  selectedCount,
  onClearSelection,
  bulkActions,
  bulkNoun,
  children,
}) => {
  const styles = useStyles();
  const { setSelectedCount, setOnClearSelection } = useSelection();
  // Slot the child DataTable portals its "Columns" toggle into, so it shares
  // the search row with the bulk-action bar. Null until the row mounts.
  const [columnsSlot, setColumnsSlot] = useState<HTMLElement | null>(null);

  // Wire the SelectionContext from the page's selection state. Mirrors the
  // boilerplate that previously lived in each list page.
  const selectionWired = selectedCount !== undefined;

  useEffect(() => {
    if (!selectionWired) return;
    setSelectedCount(selectedCount ?? 0);
  }, [selectionWired, selectedCount, setSelectedCount]);

  // Register a single stable clear-callback that always invokes the latest
  // onClearSelection via a ref — avoids re-registering (and the resulting
  // context churn) on every parent render, since onClearSelection is a fresh
  // closure each time.
  const clearRef = useRef(onClearSelection);
  clearRef.current = onClearSelection;
  useEffect(() => {
    if (!selectionWired) return;
    setOnClearSelection(() => () => clearRef.current?.());
  }, [selectionWired, setOnClearSelection]);

  useEffect(() => {
    if (!selectionWired) return;
    return () => setSelectedCount(0);
  }, [selectionWired, setSelectedCount]);

  // The bulk bar lives at the trailing edge of the search row, so render the
  // row whenever either the search slot or bulk actions are present.
  const showBulkBar = !!bulkActions && bulkActions.length > 0;
  const showSearchRow = !!searchRow || showBulkBar;

  return (
    <div className={styles.workspaceCard}>
      <Header
        inline
        hideSelectionInfo={hideSelectionInfo}
        leftActions={headerLeftActions}
        customTitle={
          typeof title === 'string' ? (
            <span
              style={{
                fontSize: tokens.fontSizeBase500,
                fontWeight: tokens.fontWeightSemibold,
                lineHeight: tokens.lineHeightBase300,
                color: tokens.colorNeutralForeground1,
              }}
            >
              {title}
            </span>
          ) : (
            title
          )
        }
        customActions={headerActions}
      />
      <div className={styles.body}>
        {sidebar}
        <div className={styles.content}>
          {loading ? (
            <div className={styles.loading}>
              <Spinner size="large" label={loadingLabel} />
            </div>
          ) : error ? (
            <div className={styles.error}>Error: {error}</div>
          ) : (
            <TableToolbarSlotProvider value={columnsSlot}>
              {showSearchRow && (
                <div className={styles.searchRow}>
                  {searchRow}
                  <div className={styles.searchRowTrailing}>
                    {/* DataTable portals its "Columns" toggle in here. */}
                    <div ref={setColumnsSlot} className={styles.columnsSlot} />
                    {showBulkBar && (
                      <BulkActionBar
                        selectedCount={selectedCount ?? 0}
                        onClear={onClearSelection ?? (() => undefined)}
                        actions={bulkActions!}
                        noun={bulkNoun}
                      />
                    )}
                  </div>
                </div>
              )}
              {children}
            </TableToolbarSlotProvider>
          )}
        </div>
      </div>
    </div>
  );
};

export default TablePage;
